// ─── Design Tokens ──────────────────────────────────────────────────────────
// Central source of truth for the GRC design system. Extend additively as
// more modules opt into the system. Import from "Shared/tokens" (webpack alias).

import { lighten, darken, alpha } from "./components/JsonForms/Layout/color-utils"

// ─── Primary Fallback ───────────────────────────────────────────────────────
// Used when the business `primaryActionColor` is not available from Redux.
export const PRIMARY_FALLBACK = "#3b82f6"
export const AI_COLOR_FALLBACK = "#7c3aed"

// ─── Colors ─────────────────────────────────────────────────────────────────
export const colors = {
  black: "#000000",
  white: "#ffffff",
  // Gray (Tailwind Gray) — primary neutral palette
  gray: {
    50: "#f9fafb", 100: "#f3f4f6", 200: "#e5e7eb", 300: "#d1d5db",
    400: "#9ca3af", 500: "#6b7280", 600: "#4b5563", 700: "#374151", 900: "#111827",
  },
  // Slate (Tailwind Slate) — cooler neutral, used in data-heavy / technical UI
  slate: {
    50: "#f8fafc", 100: "#f1f5f9", 200: "#e2e8f0", 300: "#cbd5e1",
    400: "#94a3b8", 500: "#64748b", 600: "#475569", 700: "#334155", 900: "#0f172a",
  },
  status: {
    success: { bg: "#DCFCE7", text: "#065F46", icon: "#10b981" },
    warning: { bg: "#FEF3C7", text: "#92400E", icon: "#F59E0B" },
    error: { bg: "#FEE2E2", text: "#991B1B", icon: "#EF4444" },
    info: { bg: "#DBEAFE", text: "#1E40AF", icon: "#3B82F6" },
  },
  background: "#f6f6f6", border: "#e5e7eb",
  borderInput: "#d1d5db", textPrimary: "#37352f", textSecondary: "#6b7280", textMuted: "#9ca3af",
} as const

// ─── Interaction & Opacity ──────────────────────────────────────────────────
export const interaction = { hover: 0.08, active: 0.16, disabled: 0.45, overlay: 0.5 } as const

// ─── Motion ─────────────────────────────────────────────────────────────────
export const motion = { fast: "150ms ease-in-out", normal: "250ms cubic-bezier(0.4, 0, 0.2, 1)", slow: "400ms cubic-bezier(0.4, 0, 0.2, 1)" } as const

// ─── Spacing (4px base, half-steps for common 2/6/10px patterns) ────────────
export const spacing = {
  0: 0, 0.5: 2, 1: 4, 1.5: 6, 2: 8, 2.5: 10,
  3: 12, 4: 16, 5: 20, 6: 24, 8: 32, 10: 40,
} as const

// ─── Border Radius ──────────────────────────────────────────────────────────
export const radius = { xs: 2, sm: 4, default: 6, md: 8, lg: 12, xl: 16, pill: 999 } as const

// ─── Shadows ────────────────────────────────────────────────────────────────
export const shadows = {
  sm: "0 1px 3px rgba(0, 0, 0, 0.06)",
  md: "0px 1px 5px rgba(0, 0, 0, 0.1)",
  dropdown: "0 3px 8px rgba(0, 0, 0, 0.1)",
  lg: "0 4px 12px rgba(0, 0, 0, 0.15)",
  overlay: "0 8px 24px rgba(0, 0, 0, 0.12)",
  top: "0px -2px 10px rgba(0, 0, 0, 0.16)",
  focus: "0 0 0 3px rgba(59, 130, 246, 0.18)",
} as const

// ─── Form Fields ──────────────────────────────────────────────────────────
// Single source of truth for how form controls (text inputs, textareas, selects,
// checkboxes) render. Exposed as `--field-*` CSS vars by `useBusinessTheme` so
// styled-components consume them via `var(--field-*, <fallback>)`. Tenant style
// knobs (corner radius, density) override the SAME vars — see useBusinessTheme.
// The focus ring intentionally derives from `--color-primary` (per-tenant brand),
// not a fixed hex, so keyboard focus always matches the active brand color.
export const field = {
  minHeight: 40, // hauteur de champ proportionnée au texte 14px (44 paraissait trop haut)
  paddingY: 9,
  paddingX: 12,
  radius: radius.md, // 8 — softer than the legacy 5px
  bg: colors.white, // white fill (replaces the legacy #eee "disabled-looking" grey)
  bgDisabled: colors.gray[100], // grey reserved for genuinely non-editable fields
  border: "#d7dee8", // soft blue-grey (replaces the harsh rgb(211,211,211))
  borderHover: colors.gray[300],
  text: colors.textPrimary, // #37352f — readable dark value text (was #707070)
  placeholder: colors.textMuted,
  labelColor: colors.textPrimary, // dark label — NOT the primary/brand color
  labelWeight: 600, // contrast comes from weight, not colour
  focusRingAlpha: 0.18, // opacity of the primary-coloured focus halo
} as const

// ─── Typography ─────────────────────────────────────────────────────────────
export const fontFamily = "\"Roboto\", sans-serif"
export const fontFamilyMono = "\"JetBrains Mono\", monospace"

export const typography = {
  h1: { size: 24, weight: 700, lineHeight: 1.3 },
  h2: { size: 18, weight: 600, lineHeight: 1.35 },
  h3: { size: 15, weight: 600, lineHeight: 1.4 },
  body: { size: 14, weight: 400, lineHeight: 1.5 },
  bodySmall: { size: 13, weight: 400, lineHeight: 1.4 },
  caption: { size: 12, weight: 400, lineHeight: 1.4 },
  label: { size: 12, weight: 500, lineHeight: 1.3, letterSpacing: "0.02em", textTransform: "uppercase" as const },
  xs: { size: 11, weight: 400, lineHeight: 1.35 },
  tiny: { size: 10, weight: 400, lineHeight: 1.3 },
} as const

// ─── Z-Index Layers ─────────────────────────────────────────────────────────
// Cross-component stacking order. Local z-indexes (0-10 within a single
// component) can stay as raw numbers. Anything global must use a token.
export const zIndex = {
  sticky: 550,          // AG Grid fullscreen, sticky table elements
  dropdown: 1000,       // Select menus, FilterBar popups, popovers, tooltips
  modal: 1500,          // Modal, ModalV2, PanelShell
  feature: 2000,        // Feature modals
  ai: 4200,             // AI conversation modals
  system: 9999,         // System modals
  impersonation: 10000, // Impersonation banner
} as const

// ─── Media Queries ──────────────────────────────────────────────────────────
export const media = { mobile: "@media (max-width: 480px)", tablet: "@media (max-width: 768px)", laptop: "@media (max-width: 1024px)" } as const

export { lighten, darken, alpha }
